import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { useTranslation } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const { t } = useTranslation();
  const c = t.contact;
  const form = c.form;
  const errors = c.errors;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    revenue_range: "",
    challenge: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRevenueChange = (value) => {
    setFormData((prev) => ({ ...prev, revenue_range: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.organization || !formData.revenue_range || !formData.challenge) {
      toast.error(errors.required);
      return;
    }
    if (formData.name.trim().length < 2) {
      toast.error(errors.shortName);
      return;
    }
    if (formData.organization.trim().length < 2) {
      toast.error(errors.shortOrg);
      return;
    }
    if (formData.challenge.trim().length < 10) {
      toast.error(errors.shortChallenge);
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitted(true);
      toast.success(c.success);
      setFormData({ name: "", email: "", organization: "", revenue_range: "", challenge: "" });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : "field";
        toast.error(errors.fieldError(field, first?.msg || "invalid value"));
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(errors.generic);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 relative bg-[#0A0A0B]"
      data-testid="contact-section"
    >
      <Toaster position="top-right" theme="dark" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "center" }}
            />
            <span className="fwd-eyebrow">{c.eyebrow}</span>
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "center" }}
            />
          </div>
          <h2 className="fwd-heading" data-testid="contact-headline">
            {c.headline}
          </h2>
          <p className="fwd-body mt-6 mx-auto" style={{ maxWidth: "640px" }}>
            {c.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="space-y-4"
            data-testid="contact-highlights"
          >
            {c.highlights.map((highlight, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4"
                style={{
                  background: "rgba(20, 20, 23, 0.6)",
                  border: "1px solid #2A2A2E",
                  borderRadius: "2px",
                }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(201, 169, 110, 0.1)",
                    border: "1px solid rgba(201, 169, 110, 0.3)",
                    borderRadius: "2px",
                  }}
                >
                  <Check size={14} style={{ color: "#C9A96E" }} />
                </div>
                <span className="fwd-body">{highlight}</span>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          >
            {submitted ? (
              <div
                className="p-12 text-center"
                style={{
                  background: "#141417",
                  border: "1px solid rgba(201, 169, 110, 0.3)",
                  borderRadius: "2px",
                }}
              >
                <div
                  className="w-16 h-16 mx-auto mb-6 flex items-center justify-center"
                  style={{
                    background: "rgba(201, 169, 110, 0.1)",
                    border: "1px solid rgba(201, 169, 110, 0.3)",
                    borderRadius: "2px",
                  }}
                >
                  <Check size={32} style={{ color: "#C9A96E" }} />
                </div>
                <h3 className="fwd-heading mb-3">{c.receivedTitle}</h3>
                <p className="fwd-body">{c.receivedDesc}</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-3 fwd-body"
                  style={{
                    border: "1px solid #2A2A2E",
                    color: "#F5F1E8",
                    background: "transparent",
                    borderRadius: "2px",
                  }}
                >
                  {c.submitAnother}
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="p-8 md:p-10 space-y-6"
                style={{
                  background: "#141417",
                  border: "1px solid #2A2A2E",
                  borderRadius: "2px",
                }}
                data-testid="contact-form"
              >
                <Input
                  type="text"
                  name="name"
                  placeholder={form.name}
                  value={formData.name}
                  onChange={handleChange}
                  className="h-12 bg-[#0A0A0B] border-[#2A2A2E] rounded-sm px-4 text-[#F5F1E8] placeholder:text-[#B8B8C0]/60 focus:border-[#C9A96E]/50 focus:ring-[#C9A96E]/20"
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                  data-testid="input-name"
                />
                <Input
                  type="email"
                  name="email"
                  placeholder={form.email}
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 bg-[#0A0A0B] border-[#2A2A2E] rounded-sm px-4 text-[#F5F1E8] placeholder:text-[#B8B8C0]/60 focus:border-[#C9A96E]/50 focus:ring-[#C9A96E]/20"
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                  data-testid="input-email"
                />
                <Input
                  type="text"
                  name="organization"
                  placeholder={form.organization}
                  value={formData.organization}
                  onChange={handleChange}
                  className="h-12 bg-[#0A0A0B] border-[#2A2A2E] rounded-sm px-4 text-[#F5F1E8] placeholder:text-[#B8B8C0]/60 focus:border-[#C9A96E]/50 focus:ring-[#C9A96E]/20"
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                  data-testid="input-organization"
                />
                <Select value={formData.revenue_range} onValueChange={handleRevenueChange}>
                  <SelectTrigger
                    className="h-12 bg-[#0A0A0B] border-[#2A2A2E] rounded-sm px-4 text-[#F5F1E8] focus:border-[#C9A96E]/50 focus:ring-[#C9A96E]/20"
                    style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                    data-testid="select-revenue"
                  >
                    <SelectValue placeholder={form.revenue} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141417] border-[#2A2A2E]">
                    {form.revenueRanges.map((range) => (
                      <SelectItem
                        key={range.value}
                        value={range.value}
                        className="text-[#B8B8C0] hover:bg-[#2A2A2E] focus:bg-[#2A2A2E]"
                        style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400 }}
                      >
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  name="challenge"
                  placeholder={form.challenge}
                  value={formData.challenge}
                  onChange={handleChange}
                  minLength={10}
                  rows={4}
                  className="bg-[#0A0A0B] border-[#2A2A2E] rounded-sm px-4 py-3 text-[#F5F1E8] placeholder:text-[#B8B8C0]/60 focus:border-[#C9A96E]/50 focus:ring-[#C9A96E]/20 resize-none"
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                  data-testid="textarea-challenge"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="fwd-button w-full justify-center"
                  style={{ height: "56px", borderRadius: "2px" }}
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {form.submit}
                      <ArrowRight size={18} className="fwd-arrow" />
                    </>
                  )}
                </button>
                {c.directEmail ? (
                  <p
                    className="text-center mt-4"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      color: "#B8B8C0",
                      lineHeight: 1.7,
                    }}
                    data-testid="contact-direct-email"
                  >
                    {c.directEmailLead}{" "}
                    <a
                      href={`mailto:${c.directEmail}`}
                      className="hover:text-[#E2C98A] transition-colors"
                      style={{ color: "#C9A96E" }}
                    >
                      {c.directEmail}
                    </a>
                    {c.directEmailTail}
                  </p>
                ) : null}
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
